import { FormGroup, Label, Modal, SubmitButton, Textarea } from "@xola/ui-kit";
import clsx from "clsx";
import { handleSuccessfulSubmission, isExperiencePurchaseItem, requiredString } from "common";
import { Field, FieldProps } from "formik";
import React from "react";
import { useMutation } from "react-query";
import { Purchase } from "types";
import { object } from "yup";
import { api } from "../../api/api";
import { useAuthorization } from "../../hooks/useAuthorization";
import { useInvalidate } from "../../hooks/useInvalidation";
import { Form } from "../Common/Form";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { updateTimeslotCache } from "../Event/EditEventCapacityModal";

const validationSchema = object().shape({
    text: requiredString(),
});

interface PurchaseNotePayload {
    text: string;
}

interface CreatePurchaseNoteModalProps {
    // purchase: ID | undefined;
    purchase: Purchase;
    isOpen: boolean;
    onClose: (e: any) => void;
    onContinue?: (e: any) => void;
}

export const CreatePurchaseNoteModal = ({ purchase, onClose, onContinue, isOpen }: CreatePurchaseNoteModalProps) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalContent purchase={purchase} onContinue={onContinue} onClose={onClose} />
        </Modal>
    );
};

interface ModalContentProps {
    purchase: Purchase;
    onClose: (e?: any) => void;
    onContinue?: (e?: any) => void;
}

const ModalContent = ({ purchase, onClose, onContinue }: ModalContentProps) => {
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();

    const {
        mutateAsync: addPurchaseNote,
        isLoading,
        isSuccess,
    } = useMutation(async (payload: PurchaseNotePayload) => {
        return api.post(`/purchases/${purchase.id}/notes`, payload);
    });

    const handleSubmit = async (values: any) => {
        const payload = { text: values.text.trim() };

        await addPurchaseNote(payload);

        handleSuccessfulSubmission(async () => {
            onClose();
            invalidate("purchases", purchase.id);
            invalidate("purchaseItems");
            /**
             * sometimes it takes longer for the note to be created and added to the purchase / event,
             * and this causes the old count of notes returned
             */

            for (const purchaseItem of purchase.items) {
                if (!isExperiencePurchaseItem(purchaseItem)) continue;
                const productId = purchaseItem?.product.id ?? null;
                const arrivalDate = purchaseItem?.arrivalDate ?? null;

                if (arrivalDate) {
                    const timeslotParams = {
                        timeslot: null,
                        event: null,
                        productId: productId,
                        startDate: arrivalDate,
                    };

                    const updateParams = {
                        isGranted: isGranted,
                    };

                    await updateTimeslotCache(timeslotParams, updateParams);
                }
            }
        });
        onContinue?.();
    };

    return (
        <XolaFormik
            validateOnChange
            initialValues={{ text: "" }}
            validateOnBlur={false}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit }) => (
                <Form>
                    <Modal.Header>Add Purchase Note</Modal.Header>

                    <Modal.Body>
                        <fieldset disabled={isLoading}>
                            <Field name="text">
                                {({ meta }: FieldProps) => (
                                    <FormGroup>
                                        <Label className={clsx(meta.error && "!text-danger")}>
                                            Note
                                            {meta.error ? <span className="ml-1 text-danger">{meta.error}</span> : null}
                                        </Label>
                                        <Field
                                            isRequired
                                            className="h-28"
                                            name="text"
                                            isError={!!meta.error}
                                            as={Textarea}
                                            placeholder="Message"
                                        />
                                    </FormGroup>
                                )}
                            </Field>
                        </fieldset>
                    </Modal.Body>

                    <Modal.Footer className="space-x-4">
                        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                        <SubmitButton isLoading={isLoading} isSuccess={isSuccess} onClick={handleSubmit}>
                            Add
                        </SubmitButton>
                    </Modal.Footer>
                </Form>
            )}
        </XolaFormik>
    );
};
